import { fetchAdminUserSenderSessions, forceReassignSenderSessionProxy } from '@/api/admin';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { SessionSummary } from '@/entities/sender.entity';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { PhoneIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface SenderSessionsSubtabProps {
	profileId: number;
	readOnly?: boolean;
	fetchSessions?: typeof fetchAdminUserSenderSessions;
}

export default function SenderSessionsSubtab({
	profileId,
	readOnly = false,
	fetchSessions = fetchAdminUserSenderSessions,
}: SenderSessionsSubtabProps) {
	const [sessions, setSessions] = useState<SessionSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const load = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const items = await fetchSessions(profileId, { limit: 50 });
			setSessions(items);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, [profileId, fetchSessions]);

	useEffect(() => {
		void load(true);
	}, [load]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error) {
		return (
			<LandingsErrorState
				message='Не удалось загрузить сессии sender.'
				onRetry={() => void load(true)}
			/>
		);
	}

	if (sessions.length === 0) {
		return (
			<LandingsEmptyState
				icon={PhoneIcon}
				title='Сессий нет'
				description='У пользователя нет sender-сессий.'
			/>
		);
	}

	return (
		<div className='flex flex-col gap-2'>
			{sessions.map(session => (
				<div
					key={session.id}
					className='rounded-xl border border-border bg-card px-3 py-2.5 text-sm flex items-start justify-between gap-2'
				>
					<div className='min-w-0'>
						<p className='font-medium truncate'>
							{session.label || session.username || session.phoneMasked}
						</p>
						<p className='text-xs text-muted-foreground truncate'>
							{session.phoneMasked} · {session.status}
						</p>
						<p className='text-xs text-muted-foreground font-mono truncate mt-1'>{session.id}</p>
					</div>
					{!readOnly && session.status === 'ready' ? (
						<Button
							type='button'
							size='sm'
							variant='destructive'
							className='shrink-0'
							onClick={() => {
								if (
									!window.confirm(
										'Принудительно переназначить прокси? Текущий прокси будет отпущен.',
									)
								) {
									return;
								}
								void forceReassignSenderSessionProxy(session.id)
									.then(result => {
										toast.success(`Прокси: ${result.proxyUrlMasked}`);
									})
									.catch(() => {
										toast.error('Не удалось переназначить прокси');
									});
							}}
						>
							Прокси
						</Button>
					) : null}
				</div>
			))}
		</div>
	);
}
